"use client";

import { CitationTool } from "@/components/citation-tool";
import { Inline } from "@/components/guide-body";
import {
  type ExampleColumns,
  exampleRows,
} from "@/components/tools/example-columns";
import { useToolUi } from "@/components/tools/tool-ui";
import { getFeaturedStyles } from "@/data/citation-styles";

/** English strings. A translated content file overrides these through `ui`. */
export const INTERVIEW_CITATION_GENERATOR_STRINGS = {
  calloutHeading: "Important: APA treats a personal interview differently",
  calloutBody:
    "In **APA 7th**, an interview you conducted yourself is **personal communication** — you cite it *in-text only*, e.g. `(J. Smith, personal communication, March 4, 2024)`, and it does **not** appear in the reference list. In **MLA, Chicago, and Harvard**, the same personal interview *does* get a full entry. A **published or broadcast** interview (magazine, newspaper, podcast, TV) is cited by its published form and appears in the bibliography in every style, including APA.",
  examplesHeading: "Interview Citation Examples",
  inTextPrefix: "In-text: ",
  // Columns, not rows: see `example-columns.ts`. The `example` and `inText` values here
  // are real worked citations and stay English in every language; `style` and `label`
  // translate.
  examples: {
    style: ["APA 7th edition", "MLA 9th edition", "Chicago (author-date)"],
    label: [
      "In-text only (personal communication)",
      "Works Cited",
      "Reference list",
    ],
    example: [
      "Personal interviews are NOT listed in the APA reference list — cite them only in-text.",
      "Smith, Jordan. Interview. Conducted by Alex Rivera, 4 Mar. 2024.",
      "Smith, Jordan. 2024. Interview by Alex Rivera. March 4, 2024.",
    ],
    inText: [
      "(J. Smith, personal communication, March 4, 2024)",
      "(Smith)",
      "(Smith 2024)",
    ],
  } as ExampleColumns,
} as const;

/**
 * Live citation generator embedded on the Interview landing page. Interviews have no
 * keyless metadata API, so the tool opens straight on the manual-entry tab preset to the
 * CSL `interview` type. Users type the interviewee, interviewer, date, and publication
 * and pick any of the 10,834 styles — no engine change.
 */
export default function InterviewCitationGeneratorWidget() {
  const strings = useToolUi(INTERVIEW_CITATION_GENERATOR_STRINGS);

  return (
    <>
      <CitationTool
        initialManualType="interview"
        initialMode="manual"
        styles={getFeaturedStyles()}
      />

      {/* APA personal-communication caveat — surfaced prominently so the
          generated reference-list entry is used correctly */}
      <section className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-5">
        <h2 className="font-semibold text-base text-foreground">
          {strings.calloutHeading}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed [&_strong]:text-foreground">
          <Inline text={strings.calloutBody} />
        </p>
      </section>

      {/* Examples */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.examplesHeading}
        </h2>
        <div className="space-y-4">
          {exampleRows(
            INTERVIEW_CITATION_GENERATOR_STRINGS.examples,
            strings.examples
          ).map((ex) => (
            <div
              className="rounded-lg border border-border bg-muted/30 p-4"
              key={ex.style}
            >
              <p className="mb-1 font-semibold text-foreground text-sm">
                {ex.style}
                <span className="ml-2 font-normal text-muted-foreground">
                  {ex.label}
                </span>
              </p>
              <p className="font-mono text-foreground text-sm leading-relaxed">
                {ex.example}
              </p>
              <p className="mt-2 text-muted-foreground text-sm">
                {strings.inTextPrefix}
                <span className="font-mono">{ex.inText}</span>
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
